// REACT & REACT HOOKS
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";

// STORE ACTIONS
import { editCoupon, getAllApplications, getCoupon } from "store/actions";

// COMPONENTS
import Loader from "components/shared/loader";
import SaveChangesButton from "components/Form/save-changes-button";
import FormWrapper from "components/Form/form-wrapper";
import Input from "components/Form/input";
import ReactSelect from "components/Form/react-select";
import CreatableReactSelect from "components/Form/creatable-react-select";
import { Row } from "react-bootstrap";

// HELPERS
import { toastErrorMessage } from "helpers/toaster/toastErrorMessage";
import { handleErrMsg } from "helpers/functions";

const EditCoupon = () => {
    const { formatMessage, locale } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { loading, error, singleCoupon } = useSelector(
        (state) => state.coupons
    );
    const { loading: loadingApplications, applications } = useSelector(
        (state) => state.applications
    );

    const {
        handleSubmit,
        setValue,
        register,
        control,
        watch,
        reset,
        formState: { errors },
    } = useForm();
    useEffect(() => {
        reset({
            ...singleCoupon,
            expiryDate: singleCoupon?.expiryDate?.split("T")?.[0],
        });
    }, [reset, singleCoupon]);

    // effects
    useEffect(() => {
        dispatch(getCoupon(id));
        if (!applications?.length > 0) dispatch(getAllApplications());
    }, [dispatch, id, applications?.length]);

    // error
    useEffect(() => {
        if (Object.keys(error)?.length > 0) {
            toastErrorMessage(
                formatMessage({ id: handleErrMsg(error) }),
                locale
            );
        }
    }, [error, formatMessage, locale, navigate]);

    const includedApps = watch("includedApps");
    useEffect(() => {
        if (includedApps?.length === 0)
            return setValue("includedApps", ["allApps"]);
        if (includedApps?.includes("allApps") && includedApps?.length !== 1)
            setValue("includedApps", ["allApps"]);
    }, [includedApps, setValue]);

    const onSubmit = (data) => {
        dispatch(editCoupon({ id, data, navigate }));
    };

    if (loading || loadingApplications) return <Loader />;

    return (
        <FormWrapper title={"editCoupon"} lg={12}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                    <Input
                        name="code"
                        xs="11"
                        register={register}
                        errors={errors}
                    />

                    <ReactSelect
                        name="discountType"
                        options={["percentage", "amount"]}
                        lg="6"
                        control={control}
                        errors={errors}
                    />
                    <Input
                        type="number"
                        name="discountValue"
                        langId={
                            watch("discountType") === "percentage"
                                ? "percent"
                                : "discountAmount"
                        }
                        xs="6"
                        register={register}
                        errors={errors}
                        {...(watch("discountType") === "percentage"
                            ? { max: "100" }
                            : {})}
                    />
                    <Input
                        type="number"
                        name="minimumOrderValue"
                        xs="6"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        type="number"
                        name="maximumDiscountValue"
                        xs="6"
                        register={register}
                        errors={errors}
                    />
                    <Input
                        type="date"
                        name="expiryDate"
                        xs="6"
                        register={register}
                        errors={errors}
                    />
                    <ReactSelect
                        name="status"
                        options={["active", "inactive"]}
                        lg="6"
                        control={control}
                        errors={errors}
                    />
                    <ReactSelect
                        name="usageLimit"
                        options={["limited", "unlimited"]}
                        lg="6"
                        control={control}
                        errors={errors}
                    />
                    <Input
                        type="number"
                        name="couponsCount"
                        xs="3"
                        readOnly={watch("usageLimit") === "unlimited"}
                        rules={{ required: false }}
                        register={register}
                        errors={errors}
                    />
                    <Input
                        type="number"
                        name="limitPerUser"
                        xs="3"
                        readOnly={watch("usageLimit") === "unlimited"}
                        rules={{ required: false }}
                        register={register}
                        errors={errors}
                    />

                    <ReactSelect
                        isMulti
                        name="includedApps"
                        options={["allApps", ...applications]}
                        lg="6"
                        control={control}
                        errors={errors}
                    />
                    <CreatableReactSelect
                        isMulti
                        name="beneficiaryStores"
                        options={singleCoupon?.beneficiaryStores || []}
                        lg="6"
                        control={control}
                        errors={errors}
                        rules={{ required: false }}
                    />

                    <SaveChangesButton />
                </Row>
            </form>
        </FormWrapper>
    );
};

export default EditCoupon;
